import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import moment from 'moment';
import { HelpersService } from '../../../../../../services/helpers.service';
import { RentalsService } from '../../../../../../services/rentals.service';
import { ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { ProductsService } from 'src/app/services/products.service';
import { AddEditAccessoryCategoriesComponent } from '../../../../../modals/rentals/add-edit-accessory-categories/add-edit-accessory-categories.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-add-edit-rental-accessory',
  templateUrl: './add-edit-rental-accessory.component.html',
  styleUrls: ['./add-edit-rental-accessory.component.scss'],
})
export class AddEditRentalAccessoryComponent implements OnInit {
  @Input() accessory;
  @Input() cloneAccessory;
  @Input() messageService;
  @Output() close = new EventEmitter();

  public editing: boolean = false;
  public loading: boolean = false;
  public savingRentalAccessory: boolean = false;
  public retiring: boolean = false;
  public uploadingPrimaryImage: boolean = false;
  public categoryOptions: any = [];
  public priceGroupOptions: any = [];
  public serialNumbers: any = [];
  public sizeOptions: any = [
    { value: 'N/A', label: 'N/A' },
    { value: 'XS', label: 'XS' },
    { value: 'S', label: 'S' },
    { value: 'M', label: 'M' },
    { value: 'M/L', label: 'M/L' },
    { value: 'L', label: 'L' },
    { value: 'XL', label: 'XL' },
    { value: 'XXL', label: 'XXL' },
  ];
  public modules = {
    toolbar: [
      ['bold', 'italic', 'underline'], // toggled buttons
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
      ['clean'], // remove formatting button
    ],
  };
  public countryData: any = {};
  public partnerData: any = {};
  public accessories: any = [];
  public newSku: any = null;
  public searchResult: any = [];
  public draft: boolean = false;

  constructor(
    private rentalsService: RentalsService,
    private helpersService: HelpersService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private productsService: ProductsService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    this.countryData = this.helpersService.getCountryData();
    this.partnerData = this.helpersService.getPartnerData();
    this.getAccessoryCategories();
    this.getPriceGroups();
    if (this.accessory.id) {
      this.loading = true;
      this.getRentalAccessory();
    } else {
      this.accessory.accessoryCategoryID = 0;
      this.accessory.priceGroupID = 0;
      this.accessory.quantityToCreate = 1;
      this.accessory.statusID = 10;
      this.updateSerialNumberArray();
      this.draft = true;
    }
    document.documentElement.style.setProperty('--show-intercom', 'none');
  }

  ngOnDestroy() {
    document.documentElement.style.setProperty('--show-intercom', 'inherit');
  }

  getAccessoryCategories(defaultCategory = '') {
    this.rentalsService
      .getRentalAccessoryCategories(this.helpersService.getPartnerId())
      .subscribe((res) => {
        this.categoryOptions = res.map((x) => {
          return { label: x.categoryName, value: x.id };
        });
        if (!this.accessory.id) {
          this.categoryOptions.splice(0, 0, {
            value: 0,
            label: this.translate.instant(
              'bikeshop.rentals.products.select_category',
            ),
          });
        }
        if (defaultCategory !== '') {
          var category = this.categoryOptions
            .filter((x) => {
              return x.label === defaultCategory;
            })
            .pop();
          if (category) {
            this.accessory.accessoryCategoryID = category.value;
          }
        }
      });
  }

  getPriceGroups() {
    this.rentalsService
      .getRentalPriceGroups(this.helpersService.getPartnerId())
      .subscribe((res) => {
        this.priceGroupOptions = res.map((x) => {
          return { label: x.name, value: x.id };
        });
        var priceGroupOption = this.priceGroupOptions
          .filter((x: any) => {
            return x.value == this.accessory.priceGroupID;
          })
          .pop();
        if (!priceGroupOption) {
          this.accessory.priceGroupID = 0;
          this.priceGroupOptions.splice(0, 0, {
            value: 0,
            label: this.translate.instant(
              'bikeshop.rentals.products.select_price_group',
            ),
          });
        }
      });
  }

  getRentalAccessory() {
    this.rentalsService
      .getRentalAccessory(this.helpersService.getPartnerId(), this.accessory.id)
      .subscribe((accessory) => {
        if (this.cloneAccessory && this.accessory.id) {
          this.accessory = {
            ...accessory,
            id: 0,
            bikeID: 0,
            activatedDate: moment().format('YYYY-MM-DD'),
            identifier: null,
            retiredDate: null,
            sellingPriceAfterRetirement: null,
            serialNumber: '',
            printableSerialNumber: '',
            quantityToCreate: 1,
            statusID: 10,
          };
          this.updateSerialNumberArray();
        } else {
          this.editing = true;
          this.accessory = {
            ...accessory,
            activatedDate: accessory.activatedDate
              ? moment(accessory.activatedDate).format('YYYY-MM-DD')
              : moment().format('YYYY-MM-DD'),
          };
        }

        this.loading = false;
      });
  }

  primaryUploader(event) {
    this.uploadingPrimaryImage = true;
    const reader = new FileReader();
    reader.readAsDataURL(event.target.files[0]);
    reader.onload = () => {
      this.accessory.imageURL = reader.result.toString();

      var mediaData = {
        PartnerID: this.helpersService.getPartnerId(),
        MediaData: reader.result.toString().replace(/^data:(.*;base64,)?/, ''),
      };
      this.rentalsService.uploadRentalImage(mediaData).subscribe(
        (res) => {
          this.accessory.imageURL = res.imageURL;
          this.uploadingPrimaryImage = false;
        },
        (err) => {
          this.accessory.imageURL = null;
          this.uploadingPrimaryImage = false;
        },
      );
    };
  }

  saveRentalAccessory() {
    if (this.accessory.id > 0) {
      this.updateRentalAccessory();
    } else {
      this.createRentalAccessory();
    }
  }

  updateRentalAccessory() {
    this.savingRentalAccessory = true;
    if (this.accessory.size === 'N/A') {
      this.accessory.sizeDescription = '';
    }
    this.rentalsService
      .updateRentalAccessory(this.helpersService.getPartnerId(), this.accessory)
      .subscribe(
        (res) => {
          this.close.emit();
        },
        (err) => {
          this.savingRentalAccessory = false;
        },
      );
  }

  createRentalAccessory() {
    this.savingRentalAccessory = true;
    var data = {
      ...this.accessory,
      defaultPartnerID: this.helpersService.getPartnerId(),
      serialNumbers: this.serialNumbers.map((x) => {
        return x.serialNumber;
      }),
    };
    this.rentalsService.addRentalAccessory(data).subscribe(
      (res) => {
        this.close.emit();
      },
      (err) => {
        this.savingRentalAccessory = false;
      },
    );
  }

  retireRentalAccessory() {
    this.confirmationService.confirm({
      key: 'add_edit_rental_product',
      rejectLabel: this.translate.instant('general.no'),
      acceptLabel: this.translate.instant('general.yes'),
      header: this.translate.instant(
        'bikeshop.rentals.accessories.retire_rental_accessory',
      ),
      message: this.translate.instant(
        'bikeshop.rentals.accessories.retire_rental_accessory_message',
      ),
      accept: () => {
        this.retiring = true;

        this.accessory.statusID = 100;
        this.rentalsService
          .updateRentalAccessory(
            this.helpersService.getPartnerId(),
            this.accessory,
          )
          .subscribe(
            (res) => {
              this.messageService.add({
                life: 2000,
                detail: this.translate.instant(
                  'bikeshop.rentals.products.product_has_been_retired',
                  {
                    productName:
                      this.accessory.brand + ' ' + this.accessory.name,
                  },
                ),
              });
              setTimeout(() => {
                this.close.emit();
              }, 1000);
            },
            (err) => {
              this.retiring = false;
            },
          );
      },
    });
  }

  searchAccessories(event) {
    var results = this.accessories.filter(function (product) {
      return (
        product.SKU.toLowerCase().indexOf(event.query.toLowerCase()) > -1 ||
        product.Name.toLowerCase().indexOf(event.query.toLowerCase()) > -1 ||
        (product.SystemSKU !== null &&
          product.SystemSKU.toLowerCase().indexOf(event.query.toLowerCase()) >
            -1) ||
        (product.ManufacturerSKU !== null &&
          product.ManufacturerSKU.toLowerCase().indexOf(
            event.query.toLowerCase(),
          ) > -1)
      );
    });
    results = JSON.parse(JSON.stringify(results)).map((product) => {
      product.InternalProductID = JSON.parse(JSON.stringify(product)).ID;
      product.ID = 0;
      product.Quantity = 1;
      delete product.PartnerID;
      return product;
    });

    if (results.length > 0) {
      this.searchResult = results;
    } else {
      this.searchExternal(event);
    }
  }

  searchExternal(event) {
    this.productsService
      .searchProduct(this.helpersService.getPartnerId(), event.query)
      .subscribe((results) => {
        this.searchResult = results.map((item) => {
          return {
            ID: item.ID,
            SKU: item.Description,
            Name: item.Data,
            Quantity: 1,
          };
        });
      });
  }

  linkSku(sku) {
    this.accessory.internalProductID = sku.ID;
    this.accessory.linkedInternalProductSKU = sku.SKU;
    this.accessory.linkedInternalProductName = sku.Name;
  }

  isValid() {
    return (
      this.accessory.accessoryCategoryID > 0 &&
      this.accessory.brand !== undefined &&
      this.accessory.brand.length > 0 &&
      this.accessory.name !== undefined &&
      this.accessory.name.length > 0 &&
      this.accessory.priceGroupID > 0 &&
      (this.editing || (!this.editing && this.accessory.quantityToCreate > 0))
    );
  }

  addCategory() {
    const modalRef = this.modalService.open(
      AddEditAccessoryCategoriesComponent,
      {
        backdrop: 'static',
        windowClass: 'user-modal',
      },
    );
    modalRef.componentInstance.messageService = this.messageService;
    modalRef.componentInstance.category = {};
    modalRef.result.then((result) => {
      this.getAccessoryCategories(result.categoryName);
    });
  }

  updateSerialNumberArray() {
    if (this.accessory.quantityToCreate > this.serialNumbers.length) {
      for (
        var i = 0;
        i < this.accessory.quantityToCreate - this.serialNumbers.length;
        i++
      ) {
        this.serialNumbers.push({ serialNumber: '' });
      }
    } else if (this.accessory.quantityToCreate < this.serialNumbers.length) {
      this.serialNumbers.splice(this.accessory.quantityToCreate, 100);
    }
  }
}
